import {Component, ChangeDetectionStrategy} from "@angular/core";
import {NzMessageService} from "ng-zorro-antd/message";
import {NzTableQueryParams, NzTableSortOrder} from "ng-zorro-antd/table";
import {lastValueFrom} from "rxjs";
import {
    SchemaKeywordDto,
    PagingSearchDto,
    KeywordSearchResultDto,
    PerFormaAPIService
} from "src/app/services/pfapi.service";
import {KeywordTypeLabels} from "src/app/models/performa-enums";

export interface SortDto {
    key: string;
    value: NzTableSortOrder;
}

@Component({
    selector: "app-keyword",
    templateUrl: "./keyword.component.html",
    styleUrl: "./keyword.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class KeywordComponent {
    constructor(
        private readonly api: PerFormaAPIService,
        private message: NzMessageService
    ) {}

    keywords: KeywordSearchResultDto = {
        Data: [],
        Total: 0
    } as KeywordSearchResultDto;

    loading = true;
    total = 0;
    pageSizeOptions = [10, 15, 20, 50];
    pageSize = 15;
    pageIndex = 1;

    private lastParams: NzTableQueryParams | null = null;

    // start out with a default sort order so
    // that the column header shows the correct state
    defaultSort: SortDto[] = [{key: "Name", value: "ascend"}];
    sort: SortDto[] = [...this.defaultSort];

    searchField = "Name";
    searchValue = "";
    searchFields = [{label: "Keyword Name", value: "Name"}];

    columns = [
        {
            title: "Name",
            compare: (a: SchemaKeywordDto, b: SchemaKeywordDto) => a.Name.localeCompare(b.Name),
            fieldName: "Name",
            enabled: true
        },
        {
            title: "Type",
            compare: null,
            fieldName: null,
            enabled: true
        },
        {
            title: "Validation Regex",
            compare: null,
            fieldName: null,
            enabled: true
        },
        {
            title: "Date Format",
            compare: null,
            fieldName: null,
            enabled: true
        },
        {
            title: "Unassigned",
            compare: null,
            fieldName: null,
            enabled: true
        },
        {
            title: "Edit",
            compare: null,
            fieldName: null,
            enabled: true
        }
    ];

    GetData = async () => {
        const params: PagingSearchDto = {
            PageIndex: this.pageIndex,
            PageSize: this.pageSize,
            SearchField: this.searchField || null,
            SearchValue: this.searchValue || null
        };

        const sort = this.sort.filter(s => s.value !== null) || [...this.defaultSort];

        if (sort.length) {
            params.SortField = sort[0].key;
            params.SortOrder = sort[0].value;
        }
        try {
            this.keywords = await lastValueFrom(this.api.postApiConfigureKeywordsSearch(params));
            this.total = this.keywords.Total;
        } catch (error) {
            this.message.error("Failed to fetch data");
            console.log(error);
        } finally {
            this.loading = false;
        }
    };

    async onQueryParamsChange(params: NzTableQueryParams): Promise<void> {
        if (this.isParamsSame(this.lastParams, params)) {
            return;
        }

        this.lastParams = {...params};

        this.pageIndex = params.pageIndex;
        this.pageSize = params.pageSize;

        // reset the page index if the sort changes
        if (this.SortChanged(params.sort)) {
            this.pageIndex = 1;
        }

        this.sort = [...params.sort];
        await this.GetData();
    }

    // nz-table likes to fire extraneous onQueryParamsChange events
    // so we need to filter out the ones that don't actually change anything
    private isParamsSame(a: NzTableQueryParams | null, b: NzTableQueryParams): boolean {
        if (!a) return false;
        const activeSortA = a.sort.filter(s => s.value !== null);
        const activeSortB = b.sort.filter(s => s.value !== null);
        return (
            a.pageIndex === b.pageIndex &&
            a.pageSize === b.pageSize &&
            activeSortA.length === activeSortB.length &&
            activeSortA.every((s, i) => s.key === activeSortB[i].key && s.value === activeSortB[i].value)
        );
    }

    SortChanged = (sort: SortDto[]): boolean => {
        // Filter out non-active sorts (where value is null)
        const newSort = sort.filter(s => s.value !== null);
        const currentSort = this.sort
            ? this.sort.filter(s => s.value !== null)
            : [...this.defaultSort].filter(s => s.value !== null);

        // If both are empty, no sort change
        if (newSort.length === 0 && currentSort.length === 0) {
            return false;
        }

        // If lengths differ, there's a change (e.g., one has a sort, the other doesn't)
        if (newSort.length !== currentSort.length) {
            return true;
        }

        const newActiveSort = newSort[0];
        const currentActiveSort = currentSort[0];

        return newActiveSort.key !== currentActiveSort.key || newActiveSort.value !== currentActiveSort.value;
    };

    getSortOrder(fieldName: string): NzTableSortOrder {
        if (!fieldName || !this.sort) {
            return null;
        }
        const sortItem = this.sort.find(s => s.key === fieldName);
        return sortItem ? sortItem.value : null;
    }

    async onSearch(): Promise<void> {
        this.pageIndex = 1;
        await this.GetData();
    }

    async clearSearch(): Promise<void> {
        this.searchField = "Name";
        this.searchValue = "";
        this.pageIndex = 1;
        this.sort = [...this.defaultSort];
        await this.GetData();
    }

    getKeywordTypeName(type: number): string {
        return KeywordTypeLabels[type] || "";
    }
}
